import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set up axios defaults
  useEffect(() => {
    // Set base URL for API requests
    axios.defaults.baseURL = process.env.REACT_APP_API_URL || '';
    
    // Add authorization header to all requests if token exists
    axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }, []);

  // Verify token on initial load
  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('token');
      const branch = localStorage.getItem('branch');
      if (!token || !branch) {
        setLoading(false);
        return;
      }

      try {
        // Check if token is expired
        const decoded = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        
        if (decoded.exp < currentTime) {
          // Token expired, log out
          logout();
          return;
        }

        // Verify token with backend for the correct branch
        const response = await axios.get(`/api/auth/${branch}/verify`);
        if (response.data.success) {
          setUser(response.data.user);
        } else {
          logout();
        }
      } catch (error) {
        console.error('Token verification error:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, []);

  const login = useCallback(async (username, password, branch) => {
    setError(null);
    try {
      const response = await axios.post(`/api/auth/${branch}/login`, { username, password });
      console.log('AuthContext.js login response:', response.data);
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('branch', branch); // Save branch for later use
      setUser(user);
      return true;
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Login failed');
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('branch'); // Remove branch from localStorage
    setUser(null);
  }, []);

  const isAdmin = useCallback(() => {
    return user && user.role === 'admin';
  }, [user]);

  const isStocker = useCallback(() => {
    return user && user.role === 'stocker';
  }, [user]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      error,
      login, 
      logout, 
      isAdmin, 
      isStocker 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
